import React from 'react';
import {
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    InputAdornment,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import SearchIcon from '@material-ui/icons/Search';
import TuneIcon from '@material-ui/icons/Tune';

const useStatusStyles = makeStyles((theme) => ({
    root: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: 3,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 999,
        backgroundColor: theme.palette.background.default
    },
    option: {
        minHeight: 32,
        padding: theme.spacing(0.5, 1.5),
        borderRadius: 999,
        color: theme.palette.text.secondary,
        fontSize: 12,
        fontWeight: 700,
        transition: 'background-color 120ms ease, color 120ms ease, transform 120ms ease',
        '&:hover': {color: theme.palette.text.primary, transform: 'translateY(-1px)'},
        '&:focus-visible': {outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1}
    },
    selected: {
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
    }
}));

export function StatusTabs({value, onChange, activeLabel = 'Active', inactiveLabel = 'Inactive', ariaLabel = 'Status'}) {
    const classes = useStatusStyles();
    return (
        <div className={classes.root} role="group" aria-label={ariaLabel}>
            {[
                {value: 1, label: activeLabel},
                {value: 0, label: inactiveLabel}
            ].map((option) => (
                <ButtonBase
                    key={option.value}
                    className={`${classes.option} ${parseInt(value) === option.value ? classes.selected : ''}`}
                    aria-pressed={parseInt(value) === option.value}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </ButtonBase>
            ))}
        </div>
    );
}

export const createSettingRowStyles = (theme) => ({
    row: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 220ms cubic-bezier(.2,0,0,1), box-shadow 220ms cubic-bezier(.2,0,0,1)',
        '&:last-child': {borderBottom: 0},
        '&:hover': {backgroundColor: theme.palette.action.hover}
    },
    active: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,.10)' : 'rgba(254,242,0,.16)',
        boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
        '&:hover': {backgroundColor: theme.palette.type === 'dark' ? 'rgba(254,242,0,.14)' : 'rgba(254,242,0,.22)'}
    },
    main: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: theme.spacing(3),
        minHeight: 76,
        padding: theme.spacing(1.75, 2),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
            gap: theme.spacing(1.25)
        }
    },
    title: {fontWeight: 700, fontSize: 14},
    description: {marginTop: 4, color: theme.palette.text.secondary, lineHeight: 1.5},
    action: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {width: '100%'}
    },
    details: {overflow: 'hidden'}
});

const useSettingRowStyles = makeStyles(createSettingRowStyles);

export function SettingRow({title, description, active = false, details = null, children}) {
    const classes = useSettingRowStyles();
    return (
        <div className={`${classes.row} ${active ? classes.active : ''}`} role="group" aria-label={title} data-active={active ? 'true' : 'false'}>
            <div className={classes.main}>
                <div>
                    <Typography className={classes.title}>{title}</Typography>
                    {description ? <Typography variant="body2" className={classes.description}>{description}</Typography> : null}
                </div>
                <div className={classes.action}>{children}</div>
            </div>
            {details ? <div className={classes.details}>{details}</div> : null}
        </div>
    );
}

export const createSwitchStyles = (theme) => ({
    switchBase: {
        '&$checked': {
            color: '#fff',
            transform: 'translateX(20px)',
            '& + $track': {backgroundColor: theme.palette.primary.main, opacity: 1},
            '& $thumb': {boxShadow: '0 2px 7px rgba(0,0,0,.34), 0 0 0 1px rgba(0,0,0,.12)'}
        }
    },
    checked: {},
    track: {borderRadius: 14, transition: 'background-color 220ms cubic-bezier(0.34,1.3,0.64,1), opacity 220ms ease'},
    thumb: {boxShadow: '0 2px 5px rgba(0,0,0,0.28)', transition: 'background-color 180ms ease, box-shadow 220ms ease'}
});

const useSwitchStyles = makeStyles(createSwitchStyles);

export function SettingSwitch(props) {
    const classes = useSwitchStyles();
    return <Switch classes={classes} color="default" {...props}/>;
}

const useStatusSwitchStyles = makeStyles((theme) => ({
    wrap: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 48,
        height: 30,
        verticalAlign: 'middle'
    },
    spinner: {
        color: theme.palette.text.secondary
    }
}));

// Active / inactive control shared by every list in System Settings and by User Account
export function StatusSwitch({active, pending = false, name = '', language, onToggle}) {
    const classes = useStatusSwitchStyles();
    const label = active ? language.deactivate : language.activate;
    return (
        <Tooltip title={label}>
            <span className={classes.wrap} onClick={(event) => event.stopPropagation()}>
                {pending ? (
                    <CircularProgress size={18} className={classes.spinner}/>
                ) : (
                    <Switch
                        size="small"
                        color="primary"
                        checked={active}
                        onChange={onToggle}
                        inputProps={{'aria-label': name ? `${label} ${name}` : label}}
                    />
                )}
            </span>
        </Tooltip>
    );
}

const useToolbarStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1.25),
        marginBottom: theme.spacing(2)
    },
    search: {
        flex: '1 1 240px',
        maxWidth: 420,
        '& .MuiOutlinedInput-root': {borderRadius: 12}
    },
    spacer: {flex: 1}
}));

export function SectionToolbar({search, onSearch, placeholder, children}) {
    const classes = useToolbarStyles();
    return (
        <div className={classes.toolbar}>
            {typeof search !== 'undefined' ? (
                <TextField
                    className={classes.search}
                    size="small"
                    variant="outlined"
                    value={search}
                    placeholder={placeholder || 'Search'}
                    onChange={(event) => onSearch(event.target.value)}
                    InputProps={{startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>}}
                />
            ) : null}
            <span className={classes.spacer}/>
            {children}
        </div>
    );
}

const useSurfaceStyles = makeStyles((theme) => ({
    surface: {
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 14,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.palette.type === 'dark' ? 'none' : '0 8px 24px rgba(38,42,51,0.045)'
    },
    empty: {
        display: 'flex',
        minHeight: 220,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        '& svg': {fontSize: 34, opacity: 0.55}
    }
}));

export function SettingsSurface({children, className = ''}) {
    const classes = useSurfaceStyles();
    return <div className={`${classes.surface} ${className}`}>{children}</div>;
}

export function EmptyState({label}) {
    const classes = useSurfaceStyles();
    return (
        <div className={classes.empty} role="status">
            <TuneIcon/>
            <Typography variant="body2">{label}</Typography>
        </div>
    );
}

export const createSaveStyles = (theme) => ({
    bar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        minHeight: 58,
        marginTop: theme.spacing(2),
        padding: theme.spacing(1, 1.25),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 14,
        backgroundColor: theme.palette.background.paper,
        animation: '$barIn 240ms cubic-bezier(0,0.4,0,1) both'
    },
    hint: {
        marginRight: 'auto',
        height: 24,
        border: '1px solid rgba(255,152,0,.38)',
        borderRadius: 999,
        color: theme.palette.type === 'dark' ? '#FFB74D' : '#E65100',
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,152,0,.18)' : 'rgba(255,152,0,.14)',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '.035em',
        textTransform: 'uppercase',
        '& .MuiChip-label': {paddingLeft: 10, paddingRight: 10}
    },
    hintSuccess: {
        '&&': {
            color: '#fff',
            borderColor: theme.palette.success ? theme.palette.success.main : '#2e7d32',
            backgroundColor: theme.palette.success ? theme.palette.success.main : '#2e7d32'
        }
    },
    save: {
        minWidth: 112,
        borderRadius: 10,
        fontWeight: 800,
        transition: 'min-width 220ms cubic-bezier(0.34,1.3,0.64,1), background-color 120ms ease'
    },
    success: {'&&': {color: '#fff', backgroundColor: theme.palette.success ? theme.palette.success.main : '#2e7d32'}},
    error: {animation: '$shake 300ms cubic-bezier(.36,.07,.19,.97) both'},
    '@keyframes barIn': {from: {opacity: 0, transform: 'translateY(8px)'}, to: {opacity: 1, transform: 'translateY(0)'}},
    '@keyframes shake': {
        '10%, 90%': {transform: 'translateX(-1px)'},
        '20%, 80%': {transform: 'translateX(2px)'},
        '30%, 50%, 70%': {transform: 'translateX(-3px)'},
        '40%, 60%': {transform: 'translateX(3px)'}
    }
});

const useSaveStyles = makeStyles(createSaveStyles);

export function SettingsSaveBar({dirty, status = 'idle', onSave, onReset, language}) {
    const classes = useSaveStyles();
    if (!dirty && status !== 'success') return null;
    const loading = status === 'loading';
    const success = status === 'success';
    return (
        <div className={classes.bar} role="status" aria-live="polite">
            <Chip className={`${classes.hint} ${success ? classes.hintSuccess : ''}`} size="small" label={success ? (language.operation_success || 'Saved') : (language.modified || 'Modified')}/>
            <Button disabled={loading} onClick={onReset}>{language.reset || language.cancel || 'Reset'}</Button>
            <Button
                className={`${classes.save} ${success ? classes.success : ''} ${status === 'error' ? classes.error : ''}`}
                variant="contained"
                color="primary"
                disabled={loading || success}
                onClick={onSave}
                startIcon={success ? <CheckIcon/> : null}
            >
                {loading ? <CircularProgress size={18}/> : success ? (language.saved || language.operation_success || 'Saved') : (language.save || 'Save')}
            </Button>
        </div>
    );
}
